import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Edit2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTier = {
    key: '', name: '', price_monthly: 0, price_yearly: null, min_months: 1,
    description: '', features: [], forum_access: true, events_access: true,
    library_access: false, special_events_access: false,
    connection_limit_monthly: 0, therapy_on_entry: false, therapy_monthly: false,
    is_invite_only: false, order: 100
};

export default function TiersPanel() {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [form, setForm] = useState(emptyTier);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/tiers`, { withCredentials: true });
            setTiers(data || []);
        } catch (err) { toast.error('Failed to load tiers'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setIsNew(true); setForm(emptyTier); setDialogOpen(true); };
    const openEdit = (t) => { setIsNew(false); setForm({ ...emptyTier, ...t }); setDialogOpen(true); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, features: (form.features || []).filter(f => f.trim()) };
            if (isNew) {
                await axios.post(`${API_URL}/api/admin/tiers`, payload, { withCredentials: true });
            } else {
                await axios.put(`${API_URL}/api/admin/tiers/${form.key}`, payload, { withCredentials: true });
            }
            toast.success('Tier saved');
            setDialogOpen(false);
            load();
            window.dispatchEvent(new CustomEvent('tiers-changed'));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const remove = async (key) => {
        if (!window.confirm(`Delete tier "${key}"?`)) return;
        try {
            await axios.delete(`${API_URL}/api/admin/tiers/${key}`, { withCredentials: true });
            toast.success('Tier deleted');
            load();
            window.dispatchEvent(new CustomEvent('tiers-changed'));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Delete failed');
        }
    };

    if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E] mx-auto" /></div>;

    return (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8" data-testid="tiers-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="overline mb-2">Memberships</p>
                    <h2 className="font-serif text-3xl text-[#1A1A2E]">Tiers</h2>
                    <p className="text-sm text-[#4A4A68] mt-2">Add, edit, remove tiers and their features. Deleting a tier requires no users on it.</p>
                </div>
                <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="new-tier-btn"><Plus className="w-4 h-4" /> Add tier</button>
            </div>

            <div className="space-y-3">
                {tiers.map(t => (
                    <div key={t.key} className="border border-[#E5E0D8] p-4 rounded-sm flex items-center justify-between" data-testid={`tier-row-${t.key}`}>
                        <div>
                            <p className="font-serif text-lg text-[#1A1A2E]">{t.name} <code className="text-xs bg-[#F2EBE5] px-2 py-1 rounded ml-2">{t.key}</code></p>
                            <p className="text-sm text-[#4A4A68]">{t.price_monthly}€ / month · {(t.features || []).length} features</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => openEdit(t)} className="p-2 hover:bg-[#F2EBE5] rounded" data-testid={`edit-tier-${t.key}`}><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => remove(t.key)} className="p-2 hover:bg-red-50 rounded" data-testid={`delete-tier-${t.key}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl text-[#1A1A2E]">{isNew ? 'New Tier' : `Edit ${form.name}`}</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-4">
                        {isNew && (
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Key (slug, lowercase)</label>
                                <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })} className="w-full" data-testid="tier-key" />
                            </div>
                        )}
                        <div><label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full" data-testid="tier-name" /></div>
                        <div><label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full" data-testid="tier-description" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Monthly €</label><input type="number" step="0.01" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })} className="w-full" data-testid="tier-price-monthly" /></div>
                            <div><label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Yearly €</label><input type="number" step="0.01" value={form.price_yearly ?? ''} onChange={(e) => setForm({ ...form, price_yearly: e.target.value ? parseFloat(e.target.value) : null })} className="w-full" /></div>
                            <div><label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Min months</label><input type="number" value={form.min_months} onChange={(e) => setForm({ ...form, min_months: parseInt(e.target.value) || 1 })} className="w-full" /></div>
                            <div><label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Order</label><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 100 })} className="w-full" /></div>
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Features (one per line)</label>
                            <textarea rows={4} value={(form.features || []).join('\n')} onChange={(e) => setForm({ ...form, features: e.target.value.split('\n') })} className="w-full resize-none text-sm" data-testid="tier-features" />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            {['forum_access', 'events_access', 'library_access', 'special_events_access', 'therapy_on_entry', 'therapy_monthly', 'is_invite_only'].map(k => (
                                <label key={k} className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={form[k] || false} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} />
                                    {k.replace(/_/g, ' ')}
                                </label>
                            ))}
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">Connections/month (-1 = unlimited)</label>
                            <input type="number" value={form.connection_limit_monthly} onChange={(e) => setForm({ ...form, connection_limit_monthly: parseInt(e.target.value) || 0 })} className="w-full" />
                        </div>
                        <button onClick={save} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="save-tier-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
